import { ErrorComponent, Link, Navigate, useRouter } from "@tanstack/react-router";
import Disconnected from "@repo/icons/default/Disconnected";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import Left from "@repo/icons/default/Left";
import Logout from "@repo/icons/default/Logout";
import type { AuthenticationError } from "@auth0/auth0-spa-js";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { getFeedback } from "@sentry/react";
import { MainElement } from "@/components/ui/layout/page/main-element";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  EmptyStateRoot,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateBody,
  EmptyStateButton,
} from "@/components/ui/empty-state/empty-state";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";

interface FeedbackDialog {
  open: () => void;
  close: () => void;
}

const UserErrorFeedbackButton = (): JSX.Element | null => {
  const constraintsRef = useRef<HTMLDivElement>(null);

  const [formFeedback, setFormFeedback] = useState<FeedbackDialog | undefined>(undefined);

  const feedbackError = getFeedback();
  const createFormFeedback = useCallback(async (): Promise<void> => {
    const formError = await feedbackError?.createForm();

    if (formError) {
      formError.appendToDom();
      setFormFeedback(formError);
    }
  }, [feedbackError]);

  useEffect(() => {
    void createFormFeedback();

    const setConstraintsHeight = (): void => {
      if (constraintsRef.current) {
        constraintsRef.current.style.height = `${window.innerHeight}px`;
      }
    };

    setConstraintsHeight();

    window.addEventListener("resize", setConstraintsHeight);

    return () => {
      window.removeEventListener("resize", setConstraintsHeight);
    };
  }, [createFormFeedback]);

  return formFeedback ? (
    <div className="w-full" ref={constraintsRef}>
      <EmptyStateButton
        id="feedback-button"
        onClick={() => {
          formFeedback.open();
          trackEvent("Enviar Feedback Aberto");
        }}
        fullWidth
      >
        Enviar feedback de erro
      </EmptyStateButton>
    </div>
  ) : null;
};

const SmartHomeButton = (): JSX.Element => {
  const router = useRouter();
  const currentPath = router.latestLocation.pathname;

  const isOnHomePage = currentPath === "/" || currentPath === "/home";

  if (isOnHomePage) {
    return (
      <EmptyStateButton
        fullWidth
        onClick={() => {
          window.location.reload();
        }}
      >
        Tentar novamente
      </EmptyStateButton>
    );
  }

  return (
    <Link to="/" className="w-full">
      <EmptyStateButton fullWidth>Voltar para o Início</EmptyStateButton>
    </Link>
  );
};

const LogoutButton = (): JSX.Element => (
  <Link to="/auth/logout" className="w-full" preload={false}>
    <EmptyStateButton secondary fullWidth Icon={Logout}>
      Fazer logout
    </EmptyStateButton>
  </Link>
);

interface ErrorPageProps extends React.ComponentPropsWithoutRef<"main"> {
  error: unknown;
}

const ErrorPageTemplate = ({ children }: { children: React.ReactNode }): JSX.Element => {
  return (
    <MainElement>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Livance" align="center" />
      </HeaderRoot>
      <Page>
        <EmptyStateRoot>
          <EmptyStateIcon icon={ExclamationCircle} fill="danger" />
          {children}
        </EmptyStateRoot>
      </Page>
    </MainElement>
  );
};

export const NotFoundPage = (): JSX.Element => {
  return (
    <ErrorPageTemplate>
      <EmptyStateTitle>Página não encontrada</EmptyStateTitle>
      <SmartHomeButton />
      <UserErrorFeedbackButton />
    </ErrorPageTemplate>
  );
};

export const LoginErrorPage = (): JSX.Element => {
  return (
    <ErrorPageTemplate>
      <EmptyStateTitle>Erro no login</EmptyStateTitle>
      <EmptyStateDescription>
        Usuário não autorizado. Essa aplicação é restrita para profissionais cadastrados.
        Por favor, verifique o seu cadastro.
      </EmptyStateDescription>
      <LogoutButton />
    </ErrorPageTemplate>
  );
};

export const AccessDeniedPage = ({ error }: { error: Error }): JSX.Element => {
  const cause = error.cause as string;
  const defaultMessage =
    "Não encontramos uma conta associada a esse login/e-mail. Verifique se você entrou com o usuário correto, e tente o acesso com outro login e senha.";

  return (
    <ErrorPageTemplate>
      <EmptyStateTitle>Acesso negado</EmptyStateTitle>
      <EmptyStateDescription>{cause || defaultMessage}</EmptyStateDescription>
      <LogoutButton />
    </ErrorPageTemplate>
  );
};

export const GenericErrorPage = ({ error }: ErrorPageProps): JSX.Element => {
  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <ErrorPageTemplate>
      <EmptyStateTitle>Ocorreu um erro</EmptyStateTitle>
      <EmptyStateDescription>
        Se o problema persistir, entre em contato.
      </EmptyStateDescription>
      <EmptyStateBody>
        <ErrorComponent error={error} />
        <p className="p-2">{error?.toString()}</p>
      </EmptyStateBody>
      <SmartHomeButton />
      <UserErrorFeedbackButton />
      <LogoutButton />
    </ErrorPageTemplate>
  );
};

export const NetworkErrorPage = ({ error }: ErrorPageProps): JSX.Element => {
  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <MainElement>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Livance" align="center" />
      </HeaderRoot>
      <Page>
        <EmptyStateRoot>
          <EmptyStateIcon icon={Disconnected} fill="primary" />
          <EmptyStateTitle>Problemas de conexão</EmptyStateTitle>
          <EmptyStateDescription>
            Ocorreu um erro ao processar, verifique sua conexão com a internet.
          </EmptyStateDescription>
          <EmptyStateBody>
            <ErrorComponent error={error} />
          </EmptyStateBody>
          <SmartHomeButton />
          <UserErrorFeedbackButton />
          <LogoutButton />
        </EmptyStateRoot>
      </Page>
    </MainElement>
  );
};

export const ErrorPage = ({ error }: ErrorPageProps): JSX.Element => {
  if (
    error instanceof Error &&
    Object.hasOwn(error, "error") &&
    ((error as AuthenticationError).error === "missing_refresh_token" ||
      (error as AuthenticationError).error === "invalid_grant")
  ) {
    return <Navigate params={{}} to="/auth/login" search={{ returnTo: "/" }} />;
  }

  if (error instanceof Error && error.message.includes("Usuário não autorizado")) {
    return <LoginErrorPage />;
  }

  if (error instanceof Error && error.message.includes("Acesso Negado")) {
    return <AccessDeniedPage error={error} />;
  }

  if (error instanceof TypeError && error.message.includes("Network request failed")) {
    return <NetworkErrorPage error={error} />;
  }

  return <GenericErrorPage error={error} />;
};
